import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import type { NewsCategoriesByType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoriesByType";
import { NewsCategory } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategory";
import type NewsSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalFilters";
import NewsSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalSubType";
import NewsSignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalTemplate";
import newsSignalTimeframeConfig from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalTimeframe";
import type { ICountryObject } from "app/services/CountryService";

import SignalTemplatesManager from "../../../models/templates/SignalTemplatesManager";

const UNKNOWN_ALIAS = "unknown-signal";
const UNKNOWN_SIGNAL_TRANSLATABLE = { key: "Unknown signal" };

export default class NewsSignalTemplatesManager extends SignalTemplatesManager<NewsSignalFilters> {
    constructor(
        countries: readonly ICountryObject[],
        protected readonly newsCategories: Readonly<NewsCategoriesByType>,
    ) {
        super(countries);
    }

    getEmptyTemplate(): Readonly<NewsSignalTemplate> {
        return new NewsSignalTemplate({
            signalSubType: NewsSignalSubType.AnyNews,
            signalSource: SignalSource.Platform,
            filters: {
                ...NewsSignalTemplate.defaultValues.timeFrame,
                categories: null,
            },
            alias: UNKNOWN_ALIAS,
            name: UNKNOWN_SIGNAL_TRANSLATABLE,
            description: UNKNOWN_SIGNAL_TRANSLATABLE,
        });
    }

    getDefaultTemplate(): Readonly<NewsSignalTemplate> {
        const defaultTemplate = this.getTemplates().find((template) => template.alias === "company-has-any-changes");

        return defaultTemplate ?? this.getEmptyTemplate();
    }

    getTemplates(): ReadonlyArray<NewsSignalTemplate> {
        return [
            new NewsSignalTemplate({
                alias: "company-has-leadership-changes",
                name: { key: "si.signals.templates.news.leadership.name" },
                description: {
                    key: "si.signals.templates.news.leadership.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: this.newsCategories[NewsSignalSubType.Leadership],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.Leadership,
            }),
            new NewsSignalTemplate({
                alias: "company-has-growth-changes",
                name: { key: "si.signals.templates.news.growth.name" },
                description: {
                    key: "si.signals.templates.news.growth.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: this.newsCategories[NewsSignalSubType.Growth],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.Growth,
            }),
            new NewsSignalTemplate({
                alias: "company-has-strategy-changes",
                name: { key: "si.signals.templates.news.strategy.name" },
                description: {
                    key: "si.signals.templates.news.strategy.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: this.newsCategories[NewsSignalSubType.Strategy],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.Strategy,
            }),
            new NewsSignalTemplate({
                alias: "company-has-investment-changes",
                name: { key: "si.signals.templates.news.investment.name" },
                description: {
                    key: "si.signals.templates.news.investment.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: [NewsCategory.ReceivesFinancing],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.Investment,
            }),
            new NewsSignalTemplate({
                alias: "company-has-challenges-changes",
                name: { key: "si.signals.templates.news.company-challenges.name" },
                description: {
                    key: "si.signals.templates.news.company-challenges.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: this.newsCategories[NewsSignalSubType.CompanyChallenges],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.CompanyChallenges,
            }),
            new NewsSignalTemplate({
                alias: "company-has-recognition-changes",
                name: { key: "si.signals.templates.news.recognition.name" },
                description: {
                    key: "si.signals.templates.news.recognition.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: this.newsCategories[NewsSignalSubType.Recognition],
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.Recognition,
                timeframeConfig: newsSignalTimeframeConfig,
            }),
            new NewsSignalTemplate({
                alias: "company-has-any-changes",
                name: { key: "si.signals.templates.news.any.name" },
                description: {
                    key: "si.signals.templates.news.any.description",
                },
                filters: {
                    ...NewsSignalTemplate.defaultValues.timeFrame,
                    categories: Object.values(this.newsCategories).flat(),
                },
                signalSource: SignalSource.Platform,
                signalSubType: NewsSignalSubType.AnyNews,
            }),
        ];
    }
}
